import { useEffect, useRef, useState } from 'react';
import { useRive, Layout, Fit, Alignment, useViewModel, useViewModelInstance, useViewModelInstanceString, decodeFont } from '@rive-app/react-canvas';
import { useAssets } from '../components/AssetHandler';
import { DEVICE_PIXEL_RATIO } from '../game-engine';
import { useReferralCode } from '../hooks/useReferral';
import { formatFiat } from '~/core/utils/formatFiat';
import { useAccount } from 'wagmi';
import { motion } from 'framer-motion'
import { HexagonShape, InnerShape } from '../components/Shape';
import Loader from '~/core/components/Loader';
import { icons } from '../assets/icons/icons';
import { SHARE_WORKER_URL } from '../constants';
import { api } from '../api';
import { useToast } from '~/core/contexts/ToastContext';

interface ShareModalProps {
  multiplier: number;
  winAmount: number;
  gameId: string;
  onDismiss: () => void;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  multiplier,
  winAmount,
  gameId,
  onDismiss,
}) => {
  const { assets } = useAssets();
  const { data: ref } = useReferralCode()
  const { address } = useAccount();
  const toast = useToast()

  const [visible, setVisible] = useState(false)
  const [isSharing, setIsSharing] = useState(false)

  const bungeeFontBytes = assets?.fonts?.['bungee'];

  const { rive, RiveComponent } = useRive({
    buffer: assets?.riveFiles?.['share'],
    artboard: 'share',
    stateMachines: 'state',
    autoplay: true,
    layout: new Layout({
      fit: Fit.FitWidth,
      alignment: Alignment.Center,
    }),
    assetLoader: (asset, bytes) => {
      if (asset.cdnUuid.length > 0 || bytes.length > 0) {
        return false;
      }

      if (!asset.isFont || !bungeeFontBytes) {
        return false;
      }

      const fontBytes = new Uint8Array(bungeeFontBytes);
      decodeFont(fontBytes).then(font => {
        (asset as any).setFont(font);
        font.unref();
      });
      return true;
    },
    onLoad: () => {
      setVisible(true);
    },
    autoBind: true,
  }, { customDevicePixelRatio: DEVICE_PIXEL_RATIO });

  const viewModel = useViewModel(rive, { name: 'share' });
  const viewModelInstance = useViewModelInstance(viewModel, { useDefault: true, rive });
  
  const { setValue: setWin } = useViewModelInstanceString('win', viewModelInstance);
  const { setValue: setMultiplier } = useViewModelInstanceString('multiplier', viewModelInstance);
  const { setValue: setRef } = useViewModelInstanceString('ref', viewModelInstance);

  useEffect(() => {
    if (viewModelInstance) {
      setWin(formatFiat(winAmount))
      setMultiplier(`${multiplier.toFixed(2)}x`)
    }
  }, [viewModelInstance]);

  useEffect(() => {
    if (viewModelInstance && ref) {
      setRef(ref.referralCode);
    }
  }, [ref, viewModelInstance]);

  useEffect(() => {
    if (!address) {
      onClose();
    }
  }, [address])

  const onClose = () => {
    onDismiss();
  }

  const refShare = useRef<string | null>(null);

  const share = () => {
    const text = `Watch my Replay!\n`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(refShare.current!)}`, '_blank');
    navigator.clipboard.writeText(refShare.current!);
    toast.success('Link copied to clipboard')
  }

  const onShare = async () => {
    if (isSharing || !ref) return;

    if (refShare.current) {
      share();
      return;
    }

    setIsSharing(true);

    try {
      const payload = {
        winAmount,
        multiplier,
        refCode: ref.referralCode,
        gameId,
      };

      const { url: shareUrl, shareId } = await api.createShareLink(address, payload);

      // Warm-up: Trigger image generation in background
      fetch(`${SHARE_WORKER_URL}/img/${shareId}`);

      refShare.current = shareUrl;
      share();
    } catch (e) {
      toast.error('Share failed')
      console.error('Share failed', e);
    } finally {
      setIsSharing(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <motion.div
        className="absolute inset-0 bg-[#000]/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(5px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(5px) opacity(1)' }}
      />

      <motion.div
        className="relative w-full md:w-[700px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: visible ? 1 : 0 }}
      >
        <RiveComponent className="w-full aspect-square" />

        <div className="absolute left-0 right-0 bottom-[10%] flex justify-center">
          <button
            onPointerDown={onShare}
            disabled={isSharing || !ref}
            className='disabled:opacity-80 transition-opacity'
          >
            <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
              <div className="px-10 py-3 min-w-[140px] flex justify-center items-center h-[48px]">
                {isSharing || !ref ? (
                  <Loader stroke='white' size='20px' />
                ) : (
                  <p className="text-base font-blinko text-white">
                    Share
                  </p>
                )}
              </div>
            </InnerShape>
          </button>
        </div>

        <div className="absolute right-[15%] top-[15%]">
          <button onPointerDown={onClose}>
            <HexagonShape cornerRadius={6} strokeWidth={1}>
              <div className="flex justify-center items-center p-3">
                <img src={icons.close} className='w-7 h-7' />
              </div>
            </HexagonShape>
          </button>
        </div>
      </motion.div>
    </div>
  );
};
